<template>
	<button type="button" aria-label="Open Menu" @click="openShopMenu" class="w-5 h-5 flex items-center justify-center">
		<svg-vue icon="ham-md" class="w-6 h-6 md:w-5 md:h-5 overflow-visible" />
	</button>
	<TransitionRoot unmount :show="shopMenuOpen">
		<TransitionChild
			as="template"
			enter="transition-opacity ease-linear duration-300"
			enter-from="opacity-0"
			enter-to="opacity-100"
			leave="transition-opacity ease-linear duration-300"
			leave-from="opacity-100"
			leave-to="opacity-0"
		>
			<div class="bg-black/50 fixed inset-0 z-20" @click="handleClickoutside"></div>
		</TransitionChild>
		<TransitionChild
			as="template"
			enter="transition ease-in-out duration-300 transform"
			enter-from="-translate-x-full"
			enter-to="translate-x-0"
			leave="transition ease-in-out duration-300 transform"
			leave-from="translate-x-0"
			leave-to="-translate-x-full"
		>
			<aside class="bg-white fixed top-0 left-0 w-80 h-screen z-20">
				<div :class="['w-full h-screen relative', !activeSlideKey && 'overflow-y-scroll no-scrollbar !pb-16']">
					<div class="!py-3 !px-4 flex flex-col justify-end items-end">
						<button type="button" aria-label="Close Menu" @click="handleCloseMenu">
							<svg-vue icon="close-md" class="w-3 h-3" />
						</button>
					</div>
					<ul class="!pt-5 !pb-6 !m-0 flex flex-col border-b seint-border-secondary gap-6 !mx-4">
						<li v-for="category in sortedCategories" :key="category.title">
							<template
								v-if="
									(category.children?.length > 0 || Object.values(category.items).length > 0) &&
									category.title.toLowerCase() !== 'black friday sale'
								"
							>
								<button
									type="button"
									:aria-label="`${category.title} Nav Button`"
									@click="() => handleCategoryOpen(category.title)"
									class="flex justify-between w-full items-center"
								>
									<span class="text-base/[20px] font-medium uppercase">{{ category.title }}</span>
									<svg-vue icon="chevron-right-md" />
								</button>
							</template>
							<template v-else>
								<a
									:href="category.href"
									:aria-label="`${category.title} Link`"
									class="flex justify-between w-full items-center"
								>
									<span class="text-base/[20px] font-medium uppercase">{{ category.title }}</span>
									<svg-vue icon="chevron-right-md" />
								</a>
							</template>
							<TransitionRoot unmount :show="showChild(category.title)">
								<TransitionChild
									as="template"
									enter="transition ease-in-out duration-300 transform"
									enter-from="-translate-x-full"
									enter-to="translate-x-0"
									leave="transition ease-in-out duration-300 transform"
									leave-from="translate-x-0"
									leave-to="-translate-x-full"
								>
									<div class="absolute bg-white h-screen z-30 inset-0">
										<div class="!py-3 !px-4 flex flex-col justify-end items-end border-b seint-border-secondary">
											<div class="flex items-center justify-between w-full">
												<button type="button" aria-label="Close Menu" @click="handleCategoryMenu">
													<svg-vue icon="chevron-left-md" class="w-1.5 h-3 overflow-visible" />
												</button>
												<p class="text-base/[20px] font-medium uppercase">{{ activeSlideName }}</p>
												<button type="button" aria-label="Close Menu" @click="handleCloseMenu">
													<svg-vue icon="close-md" class="w-3 h-3" />
												</button>
											</div>
										</div>
										<template v-if="category.children?.length > 0 || Object.values(category.items).length > 0">
											<template v-if="category.children?.length > 0">
												<ul
													class="!p-0 !m-0 !mx-4 !pt-4 !pb-32 flex flex-col h-full gap-4 overflow-y-scroll no-scrollbar"
												>
													<li v-for="child in category.children" class="border-b !pb-4 seint-border-secondary">
														<a
															:href="child.href"
															:aria-label="`${child.title} Link`"
															class="text-base/[20px] font-medium uppercase"
														>
															{{ child.title }}
														</a>
														<ul class="!p-0 !m-0 !mt-4 flex flex-col gap-4">
															<li v-for="(routeData, routeName) in child.items">
																<a
																	:href="routeData.href"
																	:aria-label="`${routeName} Link`"
																	class="inline-flex items-center gap-1"
																>
																	<span class="text-sm font-normal">
																		{{ routeName }}
																	</span>
																	<span
																		v-if="
																			routeData.label.length > 0 &&
																			routeData.label !== 'Out Of Stock' &&
																			routeData.label !== 'Low Stock'
																		"
																		:class="['text-xs/[18px] font-normal', routeData.style]"
																	>
																		{{ routeData.label }}
																	</span>
																</a>
															</li>
														</ul>
													</li>
													<li>
														<a
															:href="category.href"
															:aria-label="`All ${category.title} Link`"
															class="text-sm font-normal"
														>
															All {{ category.title }}
														</a>
													</li>
												</ul>
											</template>
											<template v-else-if="Object.values(category.items).length > 0">
												<ul class="!p-0 !m-0 !ps-2">
													<li v-for="(routeData, routeName) in category.items">
														<a
															:href="routeData.href"
															:aria-label="`${routeName} Link`"
															class="inline-flex items-center !py-2.5 gap-1"
														>
															<span class="text-sm font-normal"> {{ routeName }}</span>
															<span
																v-if="
																	routeData.label.length > 0 &&
																	routeData.label !== 'Out Of Stock' &&
																	routeData.label !== 'Low Stock'
																"
																:class="['text-xs/[18px] font-normal', routeData.style]"
															>
																{{ routeData.label }}
															</span>
														</a>
													</li>
												</ul>
											</template>
										</template>
										<template v-else>
											<a :href="category.href" :aria-label="`${category.title} Link`" class="text-sm font-normal">
												{{ category.title }}
											</a>
										</template>
									</div>
								</TransitionChild>
							</TransitionRoot>
						</li>
					</ul>
					<ul class="!m-0 !p-0 flex flex-col border-b seint-border-secondary gap-4 !mx-4 !py-4">
						<li v-for="discover in sortedDiscover" :key="discover.title">
							<a
								:href="discover.href"
								:aria-label="`${discover.title} Link`"
								:target="discover.isInternal ? '_self' : '_blank'"
								class="text-sm font-normal"
							>
								{{ discover.title }}
							</a>
						</li>
					</ul>
					<ul class="!m-0 !p-0 flex flex-col gap-4 !mx-4 !py-4 ios-padding">
						<li>
							<button type="button" class="flex items-center gap-2" @click="openMarketModal">
								<svg-vue :icon="marketIcons[currentMarketKey] ?? marketIcons['generic']" />
								<span class="text-sm font-normal seint-text-primary">{{ currentMarketName }}</span>
							</button>
						</li>
					</ul>
				</div>
			</aside>
		</TransitionChild>
	</TransitionRoot>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { TransitionRoot, TransitionChild } from '@headlessui/vue'
import { storeToRefs } from 'pinia'

import { useAppStore } from '@stores'

const props = defineProps({
	navCategories: {
		type: Object,
		required: true,
	},
	discover: {
		type: Object,
		required: true,
	},
	currentMarketKey: {
		type: String,
		required: true,
	},
	currentMarketName: {
		type: String,
		required: true,
	},
	marketRegistry: {
		type: Object,
		required: true,
	},
})

const appStore = useAppStore()
const { shopMenuOpen } = storeToRefs(appStore)
const { openShopMenu, closeShopMenu, openMarketModal } = appStore

const activeSlideName = ref(null)
const activeSlideKey = ref(null)
const marketIcons = ref({
	US: 'flag-usa',
	CA: 'flag-canada',
	GB: 'flag-uk',
	UK: 'flag-uk',
	generic: 'location-pin',
})

onMounted(() => document.addEventListener('keydown', handleKeydown))
onUnmounted(() => document.removeEventListener('keydown', handleKeydown))

const sortedCategories = computed(() => Object.values(props.navCategories).sort((a, b) => a.order - b.order))
const sortedDiscover = computed(() => Object.values(props.discover).sort((a, b) => a.order - b.order))

const handleCategoryMenu = () => (activeSlideKey.value = null)
const handleCloseMenu = () => {
	handleCategoryMenu()
	closeShopMenu()
}
const handleKeydown = (e) => {
	if ((e.key === 'Escape' && shopMenuOpen.value) || activeSlideKey.value) {
		if (activeSlideKey.value) {
			handleCategoryMenu()
			return
		}
		handleCloseMenu()
	}
}
const handleClickoutside = () => {
	if (shopMenuOpen.value) {
		handleCloseMenu()
	}
}
const handleCategoryOpen = (str) => {
	const title = str.replace(/ /g, '')
	if (activeSlideKey.value === title) {
		activeSlideKey.value = null
	} else {
		activeSlideName.value = str
		activeSlideKey.value = title
	}
}
const showChild = (str) => {
	const title = str.replace(/ /g, '')
	return activeSlideKey.value === title
}
</script>
